import P5, { Vector } from "p5";
import IMesh from "./IMesh";
import { BOUNDARY } from "./Global";
import GeometricOperations from "./GeometricOperations";

interface Triplet {
	a: number;
	b: number;
	c: number;
};

function compareTriples(lhs:Triplet, rhs:Triplet): number {
	if (lhs.a < rhs.a)
		return 1;

	if (lhs.a == rhs.a && lhs.b < rhs.b)
		return 1;

	if (lhs.a == rhs.a && lhs.b == rhs.b && lhs.c < rhs.c)
		return 1;

	return -1;
  }

export default class Mesh implements IMesh {
	private _numberOfVertices: number = 0;
	private _geometricCenter: Vector = new Vector(0,0,0);
	private _numberOfTriangles: number = 0;
	private _numberOfCorners: number = 0;
	public vertices: Array<Vector> = new Array<Vector>();
	public normals: Array<Vector> = new Array<Vector>();
	public corners: Array<number> = new Array<number>();
	public opposites: Array<number> = new Array<number>();
	public loaded: boolean = false;

    // minimum coordinate, calculated once mesh is loaded then anytime g-table is updated
	public minimumCoordinate: Vector = new Vector(0,0,0);

	// maximum coordinate, calculated once mesh is loaded then anytime g-table is updated
	public maximumCoordinate: Vector = new Vector(0,0,0);
	public p5Instance!: P5;
	public geometry: P5.Geometry = new P5.Geometry();

	constructor(p5Instance: P5) {
		this.p5Instance = p5Instance;
	}
	
	public get numberOfVertices(): number {
		return this._numberOfVertices;
	}

	public get numberOfTriangles(): number {
		return this._numberOfTriangles;
	}

	public get numberOfCorners(): number {
		return this._numberOfCorners;
	}

	public get geometricCenter(): Vector {
		return this._geometricCenter;
	}

	public loadMesh(name: string, recommendedSize: number): void {
		if (this.p5Instance === undefined) {
			return;
		}
		this._numberOfTriangles = 0;
		this._numberOfVertices = 0;
		this._numberOfCorners = 0;

		this.vertices = [];
		this.corners = [];
		this.opposites = [];
		this.normals = [];

		this.loaded = false;

		var mesh = Mesh.getMeshFromServer(name);
		mesh.then((value) => {
			this.vertices = value["vertices"].map((v: any) => this.p5Instance?.createVector(v["x"], v["y"], v["z"]));
			this._numberOfTriangles = value["triangles"].length;
			value["triangles"].forEach((v: any) => {
				this.corners.push(v["a"]);
				this.corners.push(v["b"]);
				this.corners.push(v["c"]);
			});
			this._numberOfVertices = this.vertices.length;
			this._numberOfCorners = this.corners.length;
			this.computeBoundingBox();
			const reScaleFactor = recommendedSize/this.width();
			this.vertices.forEach((v: Vector) => v.mult(reScaleFactor));
			this._geometricCenter = this.computeGeometricCenter();
			//console.log(`this.geometricCenter ${this.geometricCenter}`);
			this.computeNormals();
			this.buildOTable();
			this.geometry = this.makeShapes();
			this.loaded = true;
		});
	}
	
    public create2DPlane(screenSize: number): void {
        this.vertices = [
            new P5.Vector(0,0),
            new P5.Vector(0,screenSize),
            new P5.Vector(screenSize,screenSize),
            new P5.Vector(screenSize,0)
        ];
        this._numberOfVertices = 4;
        this.corners = [0,1,2,2,3,0];
        this._numberOfTriangles = 2;
        this._numberOfCorners = 6;
		this.computeBoundingBox();
		this._geometricCenter = this.computeGeometricCenter();
        this.computeNormals();
        this.buildOTable();
        this.geometry = this.makeShapes();
    }

	public refine(): void {
		const temporaryCorners: number[] = Array(this._numberOfTriangles*12).fill(BOUNDARY);
		this.splitEdges(temporaryCorners);
		this.bulge(temporaryCorners);
		this.splitTriangles(temporaryCorners);
		this.buildOTable();
		this.computeNormals();
		this.geometry = this.makeShapes();
	}

	private static async getMeshFromServer(name: string): Promise<any> {
		const url: string = `https://functionapp120231126025253.azurewebsites.net/api/HttpExample?name=${name}`;
		const response = await fetch(url, {
			mode:  'cors' ,
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
		}});
		const result = await response.json();
		return result;
	}

	private makeShapes(): P5.Geometry {
		if (this.p5Instance === undefined) {
			return this.geometry;
		}

		this.p5Instance.beginGeometry();

			this.p5Instance.push();
				this.p5Instance.translate(-this.geometricCenter.x, -this.geometricCenter.y, -this.geometricCenter.z);
				for (let i = 0; i < this.numberOfTriangles; ++i) {
					const a: P5.Vector = this.getGeometry(i*3);
					const normalA: P5.Vector = this.getVertexNormal(i*3); 
					const b: P5.Vector = this.getGeometry(i*3+1);
					const normalB: P5.Vector = this.getVertexNormal(i*3+1);
					const c: P5.Vector = this.getGeometry(i*3+2);
					const normalC: P5.Vector = this.getVertexNormal(i*3+2);

					this.p5Instance.beginShape(this.p5Instance.TRIANGLES);
						this.p5Instance.normal(normalA.x, normalA.y, normalA.z);
						this.p5Instance.vertex(a.x, a.y, a.z);
						this.p5Instance.normal(normalB.x, normalB.y, normalB.z);
						this.p5Instance.vertex(b.x, b.y, b.z);
						this.p5Instance.normal(normalC.x, normalC.y, normalC.z);
						this.p5Instance.vertex(c.x, c.y, c.z);
					this.p5Instance.endShape();
				}

			this.p5Instance.pop();

		return this.p5Instance.endGeometry();
	}

	private computeBoundingBox(): void {
		if (this.p5Instance === undefined) {
			return;
		}
		/*
		The following code fails with
		Mesh.ts:152 Uncaught (in promise) RangeError: Maximum call stack size exceeded
    at Mesh.computeBoundingBox (Mesh.ts:152:9)
    at Mesh.ts:62:9

		this.minimumCoordinate = this.p5Instance.createVector(
			Math.min(...this.vertices.map((v: Vector) => v.x)),
			Math.min(...this.vertices.map((v: Vector) => v.y)),
			Math.min(...this.vertices.map((v: Vector) => v.z)));

		this.maximumCoordinate = this.p5Instance.createVector(
			Math.max(...this.vertices.map((v: Vector) => v.x)),
			Math.max(...this.vertices.map((v: Vector) => v.y)),
			Math.max(...this.vertices.map((v: Vector) => v.z)));

			switching over to the regular forEach loop
			*/
		this.maximumCoordinate = this.p5Instance.createVector(Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER);
		this.minimumCoordinate = this.p5Instance.createVector(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
		this.vertices.forEach((point: Vector) => {
			if (point.x < this.minimumCoordinate.x) {
				this.minimumCoordinate.x = point.x;
			}
			
			if (point.y < this.minimumCoordinate.y) {
				this.minimumCoordinate.y = point.y;
			}
		
			if (point.z < this.minimumCoordinate.z) {
				this.minimumCoordinate.z = point.z;
			}
		
			if (point.x > this.maximumCoordinate.x) {
				this.maximumCoordinate.x = point.x;
			}
			
			if (point.y > this.maximumCoordinate.y) {
				this.maximumCoordinate.y = point.y;
			}
		
			if (point.z > this.maximumCoordinate.z) {
				this.maximumCoordinate.z = point.z;
			}
		});
	}

	private computeGeometricCenter(): Vector {
		const result: Vector = this.vertices.reduce((accumulator: Vector, currentValue: Vector) => {
			accumulator.x += currentValue.x;
			accumulator.y += currentValue.y;
			accumulator.z += currentValue.z;
			return accumulator;
		}, new P5.Vector(0,0,0));

		result.div(this.numberOfVertices);
		return result;
	}

	private computeNormals(): void {
		if (this.p5Instance === undefined) {
			return;
		}
		const triangleNormals: Vector[] = [];
		for (let i = 0; i < this.numberOfTriangles; ++i) {
		  	triangleNormals.push(GeometricOperations.triNormal(this.getGeometry(i*3), this.getGeometry(i*3+1), this.getGeometry(i*3+2)));
		}
	
		// computes the vertex normals as sums of the normal vectors of incident triangles scaled by area/2
		this.normals = Array(this.numberOfVertices).fill(this.p5Instance.createVector(0,0,0));
		for (let i=0; i < this._numberOfCorners; ++i) {
			this.normals[this.getVertexId(i)].add(triangleNormals[this.getTriangleId(i)]);
		}
	  }

	private buildOTable(): void {
		this.opposites = Array(this._numberOfCorners).fill(BOUNDARY);

		const triples: Triplet[] = [];

		for (let i = 0; i < this._numberOfCorners; ++i) {
		  const nextCorner = this.getVertexId(this.getNextCornerId(i));
		  const previousCorner = this.getVertexId(this.getPreviousCornerId(i));
		  triples.push({
				a: Math.min(nextCorner,previousCorner),
				b: Math.max(nextCorner,previousCorner),
				c: i
			});
		}
		
		triples.sort(compareTriples);
		
		// just pair up the stuff
		for (let i = 0; i < this._numberOfCorners-1; ++i) {
		  const t1 = triples[i];
		  const t2 = triples[i+1];

		  if (t1.a == t2.a && t1.b == t2.b) {
			this.opposites[t1.c] = t2.c;
			this.opposites[t2.c] = t1.c;
			++i;
		  }
		}
	}

	private splitEdges(temporaryCorners: number[]): void {
		for (let corner = 0; corner < this._numberOfCorners; ++corner) {
			if (this.isBorder(corner)) {
				this.vertices.push(GeometricOperations.midVector(this.getGeometry(this.getNextCornerId(corner)), this.getGeometry(this.getPreviousCornerId(corner))));
				temporaryCorners[corner] = this.vertices.length-1;
			}
			else {
				// if this corner is the first to see the edge
				if (corner < this.getOppositeCornerId(corner)) {
					this.vertices.push(GeometricOperations.midVector(this.getGeometry(this.getNextCornerId(corner)), this.getGeometry(this.getPreviousCornerId(corner))));
					temporaryCorners[this.getOppositeCornerId(corner)] = this.vertices.length-1;
					temporaryCorners[corner] = this.vertices.length-1;
				}
			}
		}
		this._numberOfVertices = this.vertices.length;
	}

	private bulge(temporaryCorners: number[]): void {
    	for (let corner=0; corner<this._numberOfCorners; ++corner) {
			const oppositeCorner: number = this.getOppositeCornerId(corner);
			const previousCorner: number = this.getPreviousCornerId(corner);
			const nextCorner: number = this.getNextCornerId(corner);
			if (!this.isBorder(corner) && corner < oppositeCorner &&
				!this.isBorder(previousCorner) && 
				!this.isBorder(nextCorner) &&
				!this.isBorder(this.getPreviousCornerId(oppositeCorner)) &&
				!this.isBorder(this.getNextCornerId(oppositeCorner))) {

				const neighboringVector = GeometricOperations.midVector(this.getGeometry(this.leftOf(corner)), this.getGeometry(this.rightOf(corner)));
				const farNeighboringVector = GeometricOperations.midVector(this.getGeometry(this.leftOf(oppositeCorner)), this.getGeometry(this.rightOf(oppositeCorner)));
				const midNeighboringVector = GeometricOperations.midVector(neighboringVector, farNeighboringVector);
				const oppositeVector = GeometricOperations.midVector(this.getGeometry(corner), this.getGeometry(oppositeCorner));
				const vectorToAdd = new Vector(midNeighboringVector.x - oppositeVector.x, midNeighboringVector.y - oppositeVector.y, midNeighboringVector.z - oppositeVector.z);
				vectorToAdd.mult(-0.25);
				this.vertices[temporaryCorners[corner]].add(vectorToAdd);
			}
		}
	}

	private splitTriangles(temporaryCorners: number[]): void {
		this.corners.fill(BOUNDARY, this._numberOfCorners, this._numberOfCorners*4);
	
		for (let corner = 0; corner < this._numberOfCorners; corner+=3) {
			const previousCorner: number = temporaryCorners[this.getPreviousCornerId(corner)];
			const nextCorner: number = temporaryCorners[this.getNextCornerId(corner)];

			let cornerIndex: number = 3*this._numberOfTriangles+corner;
			this.corners[cornerIndex] = this.getVertexId(corner);
			this.corners[this.getNextCornerId(cornerIndex)] = previousCorner;
			this.corners[this.getPreviousCornerId(cornerIndex)] = nextCorner;
		  
			cornerIndex = 6*this._numberOfTriangles+corner;
		  	this.corners[cornerIndex] = this.getVertexId(this.getNextCornerId(corner));
		  	this.corners[this.getNextCornerId(cornerIndex)] = temporaryCorners[corner];
		  	this.corners[this.getPreviousCornerId(cornerIndex)] = previousCorner;
	
			cornerIndex = 9*this._numberOfTriangles+corner;
			this.corners[cornerIndex] = this.getVertexId(this.getPreviousCornerId(corner));
			this.corners[this.getNextCornerId(cornerIndex)] = nextCorner;
			this.corners[this.getPreviousCornerId(cornerIndex)] = temporaryCorners[corner];

			this.corners[corner] = temporaryCorners[corner];
			this.corners[this.getNextCornerId(corner)] = nextCorner;
			this.corners[this.getPreviousCornerId(corner)] = previousCorner;
		}
		this._numberOfTriangles = 4*this._numberOfTriangles;
		this._numberOfCorners = 3*this._numberOfTriangles;
	}

	private isBorder(cornerId: number): boolean {
		return this.getOppositeCornerId(cornerId) == BOUNDARY;
	}

	public getVertexNormal(cornerId: number): Vector {
		return this.normals[this.getVertexId(cornerId)];
	}

	public getGeometry(cornerId: number): Vector {
		return this.vertices[this.getVertexId(cornerId)];
	}

	private getVertexId(cornerId: number): number {
		return this.corners[cornerId];
	}

	private getOppositeCornerId(cornerId: number): number {
		return this.opposites[cornerId];
	}

	private getNextCornerId(cornerId: number): number {
		return cornerId%3 == 2? cornerId-2 : cornerId+1;
	}

	private getPreviousCornerId(cornerId: number): number {
		return cornerId%3 == 0? cornerId+2 : cornerId-1;
	}

	// shortcut to the left corner
	private leftOf(cornerId: number): number {
		return this.getOppositeCornerId(this.getNextCornerId(cornerId));
	}
	
	// shortcut to the right corner
	private rightOf(cornerId: number): number {
		return this.getOppositeCornerId(this.getPreviousCornerId(cornerId));
	}

	// shortcut from vertex index to triangle index
	private getTriangleId(cornerId: number): number {
		return Math.floor(cornerId/3);
	}

	// information about geometry
	private diag(): number {
		return Math.sqrt(this.width()*this.width()+this.height()*this.height());
	}
	  
	private width(): number {
		return Math.abs(this.maximumCoordinate.x-this.minimumCoordinate.x);
	}
	  
	private height(): number {
		return Math.abs(this.maximumCoordinate.y-this.minimumCoordinate.y);
	}

	private depth(): number {
		return Math.abs(this.maximumCoordinate.z-this.minimumCoordinate.z);
	}

}